import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import FileDropZone from "./FileDropZone";
import FilePreview from "./FilePreview";
import BannerMsg from "./BannerMsg";
import EvaluateButton from "./EvaluateButton";
import SvgMapper from "../../features/common/SvgMapper";

import {
  setFile,
  removeFile,
  evaluateSuccess,
  evaluateError,
  clearBanner,
  setReportModal,
  setSelectedInterviewId,
  setReportRequestMessage,
} from "../../../store/reportReducer";
import { addBulkStudent } from "../../../application/services/studentManagementService";

export default function UploadEvaluatePage() {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const { fileName, fileSizeText, fileUrl, bannerMsg, bannerType, openReportModal, selectedInterviewId, evaluationMode } =
    useSelector((state) => state.report);

  const [interviewId, setInterviewId] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (openReportModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [openReportModal]);

  useEffect(() => {
    if (!bannerMsg) return;

    const timer = setTimeout(() => {
      dispatch(clearBanner());
    }, 2000);

    return () => clearTimeout(timer);
  }, [bannerMsg, dispatch]);

  useEffect(() => {
    if (selectedInterviewId) {
      setInterviewId(selectedInterviewId);
    }
  }, [selectedInterviewId]);

  const resetForm = () => {
    setInterviewId("");
    setVideoUrl("");
    dispatch(removeFile());
    dispatch(clearBanner());
    dispatch(setSelectedInterviewId(null));
  };

  const fileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isValidExcelFile = (file) =>
    [".xls", ".xlsx"].some((ext) => file.name.toLowerCase().endsWith(ext));

  const isValidMp4Url = (url) => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.pathname.toLowerCase().endsWith(".mp4");
    } catch {
      return false;
    }
  };

  const uploadExcelToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "interview-evaluations");
      formData.append("file", file);

      const response = await fetch(
        "https://testservice.kraftshala.com/storage/add-external",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Upload error:", error);
      return null;
    }
  };

  const handleFile = async (file) => {
    if (!isValidExcelFile(file)) {
      dispatch(evaluateError("Only .xls or .xlsx Excel files are allowed"));
      return;
    }

    const uploadedUrl = await uploadExcelToS3(file);

    if (!uploadedUrl) {
      dispatch(evaluateError("Failed to upload Excel file"));
      return;
    }

    dispatch(
      setFile({
        name: file.name,
        sizeText: fileSize(file.size),
        url: uploadedUrl,
      })
    );
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleFile(file);
    e.target.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) await handleFile(file);
  };

  const handleEvaluate = async () => {
    if (evaluationMode === "internal") {
      if (!interviewId) {
        dispatch(evaluateError("Interview ID is required"));
        return;
      }
    } else {
      if (!videoUrl.trim()) {
        dispatch(evaluateError("Please enter interview video URL"));
        return;
      }

      if (!isValidMp4Url(videoUrl)) {
        dispatch(evaluateError("Video URL must be a valid .mp4 file"));
        return;
      }
    }

    if (!fileUrl) {
      dispatch(evaluateError("Please upload Excel file"));
      return;
    }

    setIsSubmitting(true);

    try {
      let payload;
      
      if (evaluationMode === "internal") {
        payload = {
          additional_param_name: "interview_id",
          additional_param_value: interviewId,
          document: fileUrl,
          request_type: "INTERNAL_INTERVIEW_REPORT",
        };
      } else {
        payload = {
          additional_param_name: "Rubric",
          additional_param_value: fileUrl,
          document: videoUrl,
          request_type: "EXTERNAL_INTERVIEW_REPORT",
        };
      }

      await addBulkStudent(payload);
      dispatch(setReportRequestMessage("Request accepted. Evaluation will be available shortly."));
      dispatch(evaluateSuccess());
      resetForm();
      dispatch(setReportModal(false));
    } catch (error) {
      console.error("Evaluate error:", error);
      dispatch(evaluateError(error?.message || "Failed to submit evaluation. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl overflow-y-auto">
        <div className="flex justify-between items-center mb-4 border-b pb-4">
          <h1 className="text-2xl font-semibold">
            {evaluationMode === "internal" 
              ? "Upload Internal Interview Evaluation" 
              : "Upload External Interview Evaluation"}
          </h1>
          <button
            onClick={() => {
              resetForm();
              dispatch(setReportModal(false));
            }}
          >
            {SvgMapper.closeIcon("w-6 h-6")}
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Evaluation Type
          </label>
          <div className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-700">
            {evaluationMode === "internal" ? "INTERNAL INTERVIEW EVALUATION" : "EXTERNAL INTERVIEW EVALUATION"}
          </div>
        </div>

        {evaluationMode === "internal" ? (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-600">
              Interview ID
            </label>
            <input
              type="text"
              value={interviewId}
              onChange={(e) => setInterviewId(e.target.value)}
              placeholder="Interview ID"
              className="w-full border px-3 py-2 rounded-md bg-gray-100 cursor-not-allowed"
              disabled
            />
          </div>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-600">
              Interview Video URL (.mp4)
            </label>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://example.com/interview.mp4"
              className="w-full border px-3 py-2 rounded-md"
            />
          </div>
        )}
        

        <FileDropZone
          fileInputRef={fileInputRef}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onBrowse={() => fileInputRef.current?.click()}
          onFileChange={handleFileChange}
        />

        <FilePreview
          fileName={fileName}
          fileSizeText={fileSizeText}
          removeFile={() => dispatch(removeFile())}
        />

        {bannerMsg && <BannerMsg type={bannerType} message={bannerMsg} />}

        <EvaluateButton onEvaluate={handleEvaluate} isSubmitting={isSubmitting} />
      </div>
    </div>
  );
}
